import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { avtar } from "utils/images";
import { BsEmojiSmile } from "react-icons/bs";
import {
  MdOutlineAttachFile,
  MdClose,
  MdPictureAsPdf,
  MdImage,
  MdSlideshow,
  MdDescription,
} from "react-icons/md";
import emojiData from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import userResources from "api/userResources";
import WhatsAppConversation from "./WhatsAppConversation";
import { useMarkConversationRead } from "./MarkAsConversationRead";
import { AiFillMessage } from "react-icons/ai";
import MessageModal from "./MessageModal";
import { selectedUser } from "utils/images";

const TwilioWhatsAppChat = ({ selectedCustomer, refreshSelectedCustomer }) => {
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const newMessagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [open, setOpen] = useState(false);
  // document
  // const [documentFileName, setDocumentFileName] = useState("");

  // Single attachment state
  const [attachment, setAttachment] = useState(null);
  const [attachmentType, setAttachmentType] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [locationData, setLocationData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPagiantionData] = useState({});

  const fetchMessages = useCallback(async () => {
    if (!selectedCustomer?.lastWhatsappMessage?.conversation_id) {
      setMessages([]);
      return;
    }

    try {
      const res = await userResources.getSelectedUserMessages({
        conversation_id: selectedCustomer.lastWhatsappMessage.conversation_id,
        page: currentPage,
      });

      const newMessages = res.data?.message?.messages || [];
      setPagiantionData(res.data?.message?.pagination);

      setMessages((prev) => {
        // Create a map from previous messages
        const msgMap = new Map(prev.map((m) => [m.id, m]));

        // Merge new messages into the map (update if exists, add if not)
        newMessages.forEach((msg) => {
          msgMap.set(msg.id, { ...msgMap.get(msg.id), ...msg });
        });

        // Convert map back to array and sort
        return Array.from(msgMap.values()).sort((a, b) => a.id - b.id);
      });
    } catch (error) {
      console.error(error);
      setMessages([]);
    }
  }, [selectedCustomer, currentPage]);

  // Polling effect
  useEffect(() => {
    let isCancelled = false;
    let timeoutId;

    const poll = async (immediate = false) => {
      if (isCancelled) return;

      await fetchMessages(false);

      if (!isCancelled) {
        timeoutId = setTimeout(() => poll(false), 5000);
      }
    };

    // Start polling immediately when selectedCustomer changes
    poll(true);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [fetchMessages, selectedCustomer]);

  // 👇 This effect handles currentPage changes (instant fetch, no delay)
  useEffect(() => {
    fetchMessages(true); // or false depending on your param meaning
  }, [currentPage]);

  useEffect(() => {
    inputRef.current?.focus();
    setCurrentPage(1);
  }, [selectedCustomer]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setLocationData(null);
    setAttachment(file);
    setAttachmentType(
      file.type.startsWith("image")
        ? "image"
        : file.type === "application/pdf"
        ? "pdf"
        : "video"
    );
    setPreviewUrl(URL.createObjectURL(file));
    setShowOptions(false);
  };
  // powerpoint
  // Handle PowerPoint selection
  // const handleDocumentClick = () => {
  //   const input = document.createElement("input");
  //   input.type = "file";
  //   input.accept = ".doc,.docx"; // any document type you want
  //   input.onchange = (e) => {
  //     const file = e.target.files[0];
  //     if (file) {
  //       // Revoke old preview URL
  //       if (previewUrl) URL.revokeObjectURL(previewUrl);

  //       setAttachment(file); // for sending
  //       setAttachmentType("document");
  //       setDocumentFileName(file.name);

  //       // Optional: create a local URL for UI display
  //       setPreviewUrl(URL.createObjectURL(file));
  //     }
  //   };
  //   input.click();
  // };

  // Handle option clicks
  const handleOptionClick = (type) => {
    switch (type) {
      case "image":
      case "pdf":
      case "video":
        document.getElementById("file-input").accept =
          type === "image"
            ? "image/*"
            : type === "pdf"
            ? "application/pdf"
            : "video/*";
        document.getElementById("file-input").click();
        break;
      case "location":
        handleLocationSelect();
        break;
      default:
        break;
    }
  };

  // Handle location selection
  const handleLocationSelect = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      setShowOptions(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocationData({ latitude, longitude });
        setAttachment(null);
        setAttachmentType("location");
        setPreviewUrl(`https://maps.google.com/?q=${latitude},${longitude}`);
        setShowOptions(false);
      },
      () => {
        toast.error("Unable to retrieve location.");
        setShowOptions(false);
      }
    );
  };

  // Remove attachment or location
  const removeAttachment = () => {
    if (previewUrl && attachmentType !== "location")
      URL.revokeObjectURL(previewUrl);
    setAttachment(null);
    setAttachmentType("");
    setPreviewUrl("");
    setLocationData(null);
  };

  // Send message or media/location
  const sendMessage = async () => {
    const phone = selectedCustomer?.phone_number?.trim();
    const text = inputMessage.trim();
    if (!phone || (!text && !attachment && !locationData)) {
      toast.warn("Enter a message or select an attachment/location.");
      return;
    }
    setIsSending(true);
    try {
      if (attachment) {
        const data = new FormData();
        data.append("phone_number", phone);
        data.append("media", attachment);
        data.append("caption", text);
        data.append("customer_id", selectedCustomer?.id);

        const res = await userResources.getSelectedUserMediaMessages(data);
        res.status === 200
          ? toast.success("Media sent!")
          : toast.error(
              `Media send failed: ${res.data?.message || res.statusText}`
            );
        if (newMessagesEndRef.current) {
          newMessagesEndRef.current.scrollTop = 0;
        }
      } else if (locationData) {
        const link = `Location: https://maps.google.com/?q=${locationData.latitude},${locationData.longitude}`;
        const res = await userResources.sendOnlyWhatsappMsg({
          phone_number: phone,
          message: link,
          customer_id: selectedCustomer?.id,
        });
        res.status === 200 || res.success
          ? toast.success("Location sent!")
          : toast.error(
              `Location send failed: ${res.data?.message || res.statusText}`
            );
        if (newMessagesEndRef.current) {
          newMessagesEndRef.current.scrollTop = 0;
        }
      } else {
        const res = await userResources.sendOnlyWhatsappMsg({
          phone_number: phone,
          message: text,
          customer_id: selectedCustomer?.id,
        });
        res.status === 200 || res.success
          ? toast.success("Message sent!")
          : toast.error(
              `Message send failed: ${res.data?.message || res.statusText}`
            );
        if (newMessagesEndRef.current) {
          newMessagesEndRef.current.scrollTop = 0;
        }
      }
      refreshSelectedCustomer(selectedCustomer);
      setInputMessage("");
      removeAttachment();
    } catch (err) {
      console.error(err);
      // Show server error from response if available
      const errorMsg =
        err.response?.data?.message || err.message || "Unknown server error";
      toast.error(`Error sending: ${errorMsg}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleEmojiSelect = (emoji) =>
    setInputMessage((prev) => prev + emoji.native);

  // mark conversation as read
  const conversationId = selectedCustomer?.lastWhatsappMessage?.conversation_id;
  useMarkConversationRead(conversationId);

  useEffect(() => {
    setMessages([]);
  }, [selectedCustomer]);

  return (
    <div onClick={() => setShowOptions(false)} className="relative">
      {selectedCustomer ? (
        <div className="rounded-lg bg-white shadow-md dark:bg-gray-800">
          {/* Header */}
          <div className="rounded-l-none rounded-t-lg bg-gray-900 p-2 text-white">
            {!selectedCustomer ? (
              <p className="h-10 text-2xl">Welcome to Your WhatsApp Chat Hub</p>
            ) : (
              <div className="flex items-center gap-2">
                <motion.img
                  whileHover={{ scale: 1.1 }}
                  className="h-10 w-10 rounded-full object-cover"
                  src={selectedCustomer?.full_path || avtar}
                  onError={(e) => (e.target.src = avtar)}
                  alt="User"
                />
                <div>
                  <p className="text-sm">{selectedCustomer?.full_name}</p>
                  <p className="text-xs text-gray-400">
                    {selectedCustomer?.phone_number}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div
            ref={newMessagesEndRef}
            className="flex h-[500px] flex-col-reverse overflow-y-auto scroll-smooth bg-[#e5ddd5] bg-opacity-20 bg-[url('https://web.whatsapp.com/img/bg-chat-tile-dark_a4be512e7195b6b733d9110b408f075d.png')] p-4 dark:bg-[#0c1317] dark:bg-opacity-20"
          >
            <WhatsAppConversation
              messages={messages}
              ref={messagesEndRef}
              selectedCustomer={selectedCustomer}
              setCurrentPage={setCurrentPage}
              paginationData={paginationData}
            />
          </div>

          {/* Preview */}
          {previewUrl && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="relative h-full w-full">
                {attachmentType === "image" && (
                  <img
                    src={previewUrl}
                    alt="preview"
                    className="h-full w-full rounded object-contain"
                  />
                )}

                {attachmentType === "pdf" && (
                  <iframe
                    src={previewUrl}
                    title="pdf-preview"
                    className="h-full w-full rounded border"
                  />
                )}
                {/* {attachmentType === "document" && documentFileName && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg border border-blue-300 bg-blue-50 p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <MdDescription className="text-3xl text-blue-600" />

                    <div className="flex flex-col">
                      <span className="font-medium text-blue-800">
                        {documentFileName}
                      </span>
                      <span className="text-xs text-blue-500">Document</span>
                    </div>
                  </div>
                </div>
              )} */}

                {attachmentType === "video" && (
                  <video
                    src={previewUrl}
                    controls
                    className="h-full w-full rounded object-contain"
                  />
                )}

                {attachmentType === "location" && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {`Location: ${locationData.latitude}, ${locationData.longitude}`}
                  </a>
                )}

                <button
                  onClick={removeAttachment}
                  className="absolute right-4 top-4 z-10 text-gray-600 hover:text-red-500"
                >
                  <MdClose size={24} />
                </button>
              </div>
            </div>
          )}

          {/* Input & Actions */}
          <div className="relative flex items-center gap-2 border-t bg-white p-2 dark:bg-gray-800">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="text-xl text-gray-500 hover:text-blue-500"
            >
              <BsEmojiSmile />
            </button>

            <div className="relative">
              <MdOutlineAttachFile
                className="cursor-pointer text-gray-600"
                size={24}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOptions((prev) => !prev);
                }}
              />
              {showOptions && (
                <div className="absolute bottom-10 left-0 z-10 flex flex-col gap-2 rounded-md bg-white p-2 shadow-md dark:bg-gray-800">
                  <button
                    className="flex items-center gap-2 rounded p-1 hover:bg-gray-100"
                    onClick={() => handleOptionClick("image")}
                  >
                    {" "}
                    <MdImage className="text-orange-500" /> Image
                  </button>
                  <button
                    className="flex items-center gap-2 rounded p-1 hover:bg-gray-100"
                    onClick={() => handleOptionClick("pdf")}
                  >
                    {" "}
                    <MdPictureAsPdf className="text-red-500" /> PDF
                  </button>

                  {/* <button
                  className="flex items-center gap-2 rounded p-1 hover:bg-gray-100"
                  onClick={handleDocumentClick}
                >
                  <MdDescription className="text-blue-500" /> Document
                </button> */}

                  {/* <button
                  className="flex items-center gap-2 rounded p-1 hover:bg-gray-100"
                  onClick={() => handleOptionClick("video")}
                >
                  {" "}
                  <MdVideoLibrary /> Video
                </button> */}
                  {/* <button
                  className="flex items-center gap-2 rounded p-1 hover:bg-gray-100"
                  onClick={() => handleOptionClick("location")}
                >
                  {" "}
                  <MdLocationOn /> Location
                </button> */}
                </div>
              )}
            </div>

            <input
              id="file-input"
              type="file"
              className="hidden"
              onChange={handleFileSelect}
            />

            <input
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type a message"
              className="flex-grow rounded-full bg-gray-100 px-3 py-1 focus:outline-none dark:bg-gray-700 dark:text-white"
            />

             {/* <div className="">
              Message Icon
              <AiFillMessage
                className="h-6 w-6 cursor-pointer text-blue-600"
                onClick={() => setOpen(true)}
              />

              Modal Component
              <MessageModal
                isOpen={open}
                onClose={() => setOpen(false)}
                selectedCustomer={selectedCustomer}
              />
            </div> */}

            <button
              onClick={sendMessage}
              disabled={isSending}
              className="rounded-full bg-blue-500 px-4 py-1 text-white"
            >
              {isSending ? "..." : "Send"}
            </button>
          </div>

          {showEmojiPicker && (
            <div className="absolute bottom-16 right-4">
              <Picker data={emojiData} onEmojiSelect={handleEmojiSelect} />
            </div>
          )}
        </div>
      ) : (
        <div>
          <img src={selectedUser} alt="Please select a customer to start messaging."/>
        </div>
      )}
    </div>
  );
};

export default TwilioWhatsAppChat;
